import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SelectivePreloadStrategy implements PreloadingStrategy {
  
  // High priority modules that should be preloaded immediately
  private highPriorityRoutes = [
    'business',
    'newjobs',
    'newcandidates',
    'notifications'
  ];
  
  // Medium priority modules that should be preloaded after a delay
  private mediumPriorityRoutes = [
    'networks',
    'communities',
    'teams',
    'message'
  ];
  
  // Low priority modules that should only be preloaded on user interaction
  private lowPriorityRoutes = [
    'user-management'
  ];

  preload(route: Route, load: () => Observable<any>): Observable<any> {
    // Don't preload if user is on slow connection
    if (this.isSlowConnection()) {
      return of(null);
    }

    const routePath = route.path;
    
    // Check if route has preload data
    if (route.data && route.data['preload'] === false) {
      return of(null);
    }
    
    // High priority - preload immediately
    if (routePath && this.highPriorityRoutes.includes(routePath)) {
      console.log(`🚀 Preloading high-priority route: ${routePath}`);
      return load();
    }
    
    // Medium priority - preload after 2 seconds
    if (routePath && this.mediumPriorityRoutes.includes(routePath)) {
      return new Observable(observer => {
        setTimeout(() => {
          console.log(`⚡ Preloading medium-priority route: ${routePath}`);
          load().subscribe(observer);
        }, 2000);
      });
    }
    
    // Low priority - preload after user interaction (mouse movement or scroll)
    if (routePath && this.lowPriorityRoutes.includes(routePath)) {
      return new Observable(observer => {
        const preloadOnInteraction = () => {
          console.log(`🐌 Preloading low-priority route: ${routePath}`);
          load().subscribe(observer);
          document.removeEventListener('mousemove', preloadOnInteraction);
          document.removeEventListener('scroll', preloadOnInteraction);
        };
        
        document.addEventListener('mousemove', preloadOnInteraction, { once: true });
        document.addEventListener('scroll', preloadOnInteraction, { once: true });
        
        // Fallback - preload after 10 seconds if no interaction
        setTimeout(() => {
          if (document.body.contains(document.querySelector('[data-preloaded="' + routePath + '"]'))) {
            return;
          }
          preloadOnInteraction();
        }, 10000);
      });
    }
    
    // Default behavior - don't preload unknown routes
    return of(null);
  }
  
  private isSlowConnection(): boolean {
    // Check if Network Information API is available
    const connection = (navigator as any)?.connection;
    if (connection) {
      // Don't preload on 2G connections or when save-data is enabled
      return connection.effectiveType === '2g' || connection.saveData;
    }
    
    // Fallback - assume good connection if API not available
    return false;
  }
}